import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Inject,
  OnDestroy,
  OnInit,
  DOCUMENT,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { TranslateService } from '@ngx-translate/core';
import { Confirmation, ConfirmationService } from 'primeng/api';
import { filter, map } from 'rxjs';
import { CustomEventTokens } from 'src/app/shared/models/custom-event-tokens';
import { CustomEvent } from 'src/app/shared/models/custom-event.interface';
import { LocalStorageService } from 'src/app/shared/services/local-storage/local-storage.service';
import { environment } from 'src/environments/environment';
import { HeaderMessageStateService } from './shared/components/header/services/header-message-state.service';
import { LeftSidebarMenuService } from './shared/components/left-sidebar-menu/services/left-sidebar-menu.service';
import { PermissionService } from './shared/guards/permission/service/permission.service';
import { RouterUrlType } from './shared/models/routes-urls.type.enum';
import { User } from './shared/models/user.interface';
import { AUTHENTICATION_STATUS } from './shared/services/authentication/authentication-utilities.service';
import { AuthenticationService } from './shared/services/authentication/authentication.service';
import { CustomEventsService } from './shared/services/custom-events/custom-events.service';
import { EnumerationsService } from './shared/services/enumerations/enumerations.service';
import { GetLoggedInUserBannerGQL } from './graphql/generated';
import { GraphqlApisService } from './shared/services/graphql-apis/graphql-apis.service';
import { I18nService } from './shared/services/i18n/i18n.service';
import { ProgressSpinnerService } from './shared/services/progress-spinner/progress-spinner.service';
import { ToastMessageService } from './shared/services/toast-message.service';
import { UserNotificationService } from './shared/services/user-notification/user-notification.service';
import { UserService } from './shared/services/user.service';
import { AnalyticsService } from './analytics/services/analytics/analytics.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  isCloseSidebar: boolean = false;
  currentLanguage!: string;
  environmentProfile!: string;
  isFundingPipelinePage: boolean = false;
  isReportingPage: boolean = false;
  _showPermissionsBanner: boolean = false;
  bannerMessage: { visible: boolean; text: string } = {
    visible: false,
    text: '',
  };
  startProgressSpinner: boolean = false;
  currentTimeout: number = 0;
  hasError = false;
  confirmationObj: Confirmation = {
    header: 'Inactivity Timeout',
    message: '',
    acceptLabel: 'Continue',
    rejectLabel: 'Sign Out',
    defaultFocus: 'accept',
    rejectButtonStyleClass: 'p-button-outlined button-secondary',
    acceptIcon: 'pi',
    rejectIcon: 'pi',
    accept: () => {
      this.resetIdleState();
    },
    reject: (event: number) => {
      if (event === 1) {
        this.signOutFromInactiveConfirmationDialog();
      } else {
        this.resetIdleState();
      }
    },
    key: 'inactivityDialog',
  };
  isSidebarExpanded$ =
    this.leftSidebarMenuService.sidebarPersistenceState$.pipe(
      map((state) => state.isSidebarExpanded)
    );
  showPermissionsBanner$ = this.permissionService.getShowPermissionsBanner();
  customTenantMessageVisibility$ =
    this.headerMessageStateService.getCustomTenantMessageVisibility();
  isAuthenticated$ = this.authenticationService.authState$.pipe(
    map((authState) => authState.status === AUTHENTICATION_STATUS.AUTHENTICATED)
  );
  isAuthenticationError$ = this.authenticationService.authState$.pipe(
    map((authState) => authState.status === AUTHENTICATION_STATUS.ERROR)
  );
  isUnAuthenticated$ = this.authenticationService.authState$.pipe(
    map(
      (authState) => authState.status === AUTHENTICATION_STATUS.UNAUTHENTICATED
    )
  );

  constructor(
    private readonly analyticsService: AnalyticsService,
    private readonly authenticationService: AuthenticationService,
    private readonly cd: ChangeDetectorRef,
    private readonly confirmationService: ConfirmationService,
    private readonly customEventsService: CustomEventsService,
    private readonly destroyRef: DestroyRef,
    private readonly enumerationsService: EnumerationsService,
    private readonly getLoggedInUserBannerGql: GetLoggedInUserBannerGQL,
    private readonly graphqlApisService: GraphqlApisService,
    @Inject(DOCUMENT) private readonly _document: any,
    private readonly headerMessageStateService: HeaderMessageStateService,
    private readonly i18nService: I18nService,
    private readonly idle: Idle,
    private readonly leftSidebarMenuService: LeftSidebarMenuService,
    private readonly localStorageService: LocalStorageService,
    public readonly permissionService: PermissionService,
    private readonly progressSpinnerService: ProgressSpinnerService,
    private readonly router: Router,
    private readonly toastMessageService: ToastMessageService,
    private readonly translateService: TranslateService,
    private readonly userNotificationService: UserNotificationService,
    private readonly userService: UserService
  ) {
    this.configureIdleSetup();
  }

  ngOnInit(): void {
    this.environmentProfile = environment.profile;
    // Initialize Google Analytics
    this.analyticsService.initialize();

    this.translateService.onLangChange
      .pipe(
        map((evt) => evt.lang),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((lang) => (this._document.documentElement.lang = lang));

    this.authenticationService.initializeAuthState(this.destroyRef);

    this.authenticationService.authState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((authState) => {
        switch (authState.status) {
          case AUTHENTICATION_STATUS.AUTHENTICATED:
            this.getEnumerations();
            this.onLanguageChange();
            this.onLogin();
            break;
          case AUTHENTICATION_STATUS.UNAUTHENTICATED:
            this.onLogout();
            break;
          case AUTHENTICATION_STATUS.ERROR:
            this.hasError = true;
            break;
          default:
            break;
        }
      });

    this.customEventsService
      .getEvent()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((customEvent: CustomEvent) => {
        if (customEvent.name === CustomEventTokens.CHANGE_LANGUAGE) {
          this.onLanguageChange();
        } else if (
          customEvent.name === CustomEventTokens.CREATE_TENANT_SUCCESS
        ) {
          this.onCreateTenantSuccess();
        } else if (
          customEvent.name === CustomEventTokens.CREATE_IMB_PROFILE_SUCCESS
        ) {
          this.onImbProfileSuccess();
        } else if (
          customEvent.name === CustomEventTokens.UPDATE_MESSAGE_BANNER
        ) {
          this.getUserInfo();
        }
      });

    this.i18nService
      .getLanguage()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((language) => {
        this.currentLanguage = language;
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: any) => {
        this.isFundingPipelinePage = (event?.urlAfterRedirects).includes(
          'funding-pipeline'
        )
          ? true
          : false;

        this.isReportingPage = (event?.urlAfterRedirects).includes(
          'reporting/reporting'
        )
          ? true
          : false;
        this.checkPermissions(event?.urlAfterRedirects);
      });

    this.showPermissionsBanner();

    this.resetIdleState();
    this.progressSpinnerService.progressSpinnerState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loader: boolean) => {
        this.startProgressSpinner = loader;
      });
  }

  onLogin(): void {
    this.toastMessageService.clear();
    this.getUserInfo();
    this.userNotificationService.setSessionActive(true);
    this.userNotificationService.connect();
  }

  onLogout(): void {
    this.userNotificationService.setSessionActive(false);
    this.stopIdle();
    this.i18nService.setLanguage(CustomEventTokens.ENGLISH_LANGUAGE);
  }

  onCreateTenantSuccess(): void {
    this.toastMessageService.success('', 'Tenant Created Successfully');
  }
  onImbProfileSuccess(): void {
    this.toastMessageService.success('', 'Profile Created Successfully');
  }

  onLanguageChange(): void {
    if (this.currentLanguage !== undefined) {
      const user: User = this.getUser();
      user.language = this.currentLanguage;
      this.localStorageService.createLocalStorageItem(
        CustomEventTokens.AGILITY_USER,
        user
      );
      this.i18nService.saveLanguage(this.currentLanguage);
    }
  }

  getUser(): User {
    return this.localStorageService.getLocalStorageItem(
      CustomEventTokens.AGILITY_USER
    );
  }

  ngOnDestroy(): void {
    this.stopIdle();
  }

  getEnumerations() {
    this.graphqlApisService.getEnumerations().subscribe(({ data }: any) => {
      if (data?.getEnumerations) {
        const user = this.localStorageService.getLocalStorageItem(
          CustomEventTokens.AGILITY_USER
        );
        user.enumerations = data?.getEnumerations;
        this.localStorageService.createLocalStorageItem(
          CustomEventTokens.AGILITY_USER,
          user
        );
        this.enumerationsService.updateEnumerations();
      }
    });
  }

  closeSidebar(event: any) {
    this.isCloseSidebar = event;
    this.userService.setIsCloseSidebar(this.isCloseSidebar);
  }

  checkPermissions(urlAfterRedirects: string) {
    let user: any = this.getUser();
    if (!user?.claims) return;
    let userType = user.claims.userType;
    if (urlAfterRedirects?.includes(RouterUrlType.FUNDING_PIPELINE)) {
      if (userType === 'SUB_TENANT') {
        this.setPermissionsBannerFlag([
          'ADMIN',
          'CREATE_LOAN',
          'EDIT_LOAN_DETAILS',
          'LOAN_REQUESTS',
        ]);
      }
      if (userType === 'TENANT') {
        this.setPermissionsBannerFlag(['ADMIN', 'LOAN_REQUESTS']);
      }
    } else if (
      urlAfterRedirects?.includes(RouterUrlType.FUNDING_LOAN_REQUEST)
    ) {
      if (userType === 'SUB_TENANT') {
        this.setPermissionsBannerFlag([
          'UPLOAD_LOAN_DOCS',
          'CREATE_LOAN',
          'DESIGNATE_HIGH_PRIORITY',
          'ADMIN',
          'VIEW_SENSITIVE_LOAN_DATA',
        ]);
      }
      if (userType === 'TENANT') {
        this.setPermissionsBannerFlag(['ADMIN', 'VIEW_SENSITIVE_LOAN_DATA']);
      }
    } else if (urlAfterRedirects?.includes(RouterUrlType.FUNDING_LOAN_VIEW)) {
      if (userType === 'SUB_TENANT') {
        this.setPermissionsBannerFlag([
          'ADMIN',
          'CREATE_LOAN',
          'LOAN_REQUESTS',
          'DESIGNATE_HIGH_PRIORITY',
          'ADJUST_ACCOUNTING_TRANSACTIONS',
          'INPUT_FEDREF_AT_LOAN_LEVEL',
          'EDIT_LOAN_DETAILS',
          'UPLOAD_LOAN_DOCS',
          'VIEW_SENSITIVE_LOAN_DATA',
          'REQUEST_ADDITIONAL_FUNDS',
        ]);
      }
      if (userType === 'TENANT') {
        this.setPermissionsBannerFlag([
          'ADMIN',
          'ADJUST_ACCOUNTING_TRANSACTIONS',
          'INPUT_FEDREF_AT_LOAN_LEVEL',
          'VIEW_SENSITIVE_LOAN_DATA',
          'OVERRIDE_FUNDING_CONDITIONS',
          'FUND_LOANS',
          'REQUEST_ADDITIONAL_FUNDS',
        ]);
      }
    } else if (urlAfterRedirects?.includes(RouterUrlType.COLLATERAL)) {
      if (userType === 'TENANT') {
        this.setPermissionsBannerFlag([
          'CHANGE_COLLATERAL_STATUS',
          'SHIP_COLLATERAL',
        ]);
      } else {
        this.permissionService.setShowPermissionsBanner(false);
      }
    } else if (urlAfterRedirects?.includes(RouterUrlType.DASHBOARD)) {
      let permissions: Array<string> = [];
      if (userType === 'TENANT') {
        permissions = [
          'FUNDING_DASHBOARD',
          'COLLATERAL_DASHBOARD',
          'MANAGEMENT_DASHBOARD',
          'EXECUTIVE_DASHBOARD',
        ];
      }
      if (userType === 'SUB_TENANT') {
        permissions = [
          'FUNDING_DASHBOARD',
          'COLLATERAL_DASHBOARD',
          'CREATE_LOAN',
          'ADMIN',
        ];
      }
      this.setPermissionsBannerFlag(permissions);
    } else if (
      urlAfterRedirects?.includes(RouterUrlType.ADMIN_GENERAL_SETUP) ||
      urlAfterRedirects?.includes(RouterUrlType.ADMIN_ORIGINATOR_PROFILE)
    ) {
      this.setPermissionsBannerFlag(['ADMIN']);
    } else if (urlAfterRedirects?.includes(RouterUrlType.FINANCING_SOURCES)) {
      if (userType !== 'TENANT') {
        this.permissionService.setShowPermissionsBanner(false);
      } else {
        this.setPermissionsBannerFlag(['ADMIN']);
      }
    } else if (
      urlAfterRedirects?.includes(RouterUrlType.ADMIN_WIRE_RECIPIENT_PROFILE) ||
      urlAfterRedirects?.includes(RouterUrlType.ADMIN_WIRE_RECIPIENT_SETUP)
    ) {
      this.setPermissionsBannerFlag(['CREATE_EDIT_WIRE_RECIPIENT']);
    } else if (urlAfterRedirects?.includes(RouterUrlType.TREASURY)) {
      this.setPermissionsBannerFlag(['WITHDRAW_DEPOSIT']);
    } else if (
      urlAfterRedirects?.includes(RouterUrlType.ADMIN_SHIPPING) ||
      urlAfterRedirects?.includes(
        RouterUrlType.ADMIN_WIRE_RECIPIENT_VERIFICATION
      )
    ) {
      this.setPermissionsBannerFlag(['CONFIGURE_AGILITY_CONNECT']);
    } else if (urlAfterRedirects?.includes(RouterUrlType.ADMIN_CONDITIONS)) {
      this.setPermissionsBannerFlag([
        'OVERRIDE_FUNDING_CONDITIONS',
        'OVERRIDE_COLLATERAL_CONDITION',
        'OVERRIDE_SETTLEMENT_CONDITION',
        'OVERRIDE_SHIPPIG_CONDITION',
        'CONDITION_CONFIGURATION',
      ]);
    } else if (urlAfterRedirects?.includes(RouterUrlType.SETTLEMENT_LANDING)) {
      if (this.userService.isSubTenant) {
        this.setPermissionsBannerFlag([
          'MANAGE_SETTLEMENT_WIRES',
          'ADD_LOANS_TO_WIRE',
          'SUBMIT_SETTLEMENT_REQUEST',
        ]);
      } else if (this.userService.isTenant) {
        this.setPermissionsBannerFlag([
          'ADMIN',
          'MANAGE_SETTLEMENT_WIRES',
          'ADD_LOANS_TO_WIRE',
          'SETTLE_LOANS',
          'SUBMIT_SETTLEMENT_REQUEST',
        ]);
      }
    } else if (urlAfterRedirects?.includes(RouterUrlType.ORIGINATORS_SUMMARY)) {
      this.setPermissionsBannerFlag(['CREATE_EDIT_IMB_LINE_AND_PRODUCTS']);
    } else if (urlAfterRedirects?.includes(RouterUrlType.INDEX_SETUP)) {
      this.setPermissionsBannerFlag(['CREATE_EDIT_INDEX']);
    } else if (urlAfterRedirects?.includes(RouterUrlType.INVESTOR_SUMMARY)) {
      this.setPermissionsBannerFlag(['CREATE_EDIT_INVESTOR_DETAILS']);
    } else if (urlAfterRedirects?.includes(RouterUrlType.SUPER_ADMIN_TENANTS)) {
      this.setPermissionsBannerFlag(['SUPERUSER_ADMIN']);
    } else {
      this.permissionService.setShowPermissionsBanner(false);
    }
  }
  setPermissionsBannerFlag(permissions: string[]) {
    const flag = permissions.every((permission) =>
      this.permissionService.hasPermission({ permission: permission })
    );
    this.permissionService.setShowPermissionsBanner(!flag);
  }
  showPermissionsBanner() {
    this.permissionService
      .getShowPermissionsBanner()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((flag: boolean) => {
        this._showPermissionsBanner = flag;
      });
  }
  onClosePermissionsBanner() {
    this.permissionService.setShowPermissionsBanner(false);
  }

  // Get the user info from the graphql api and set the banner message visibility and text
  getUserInfo() {
    if (!this.userService.isSuperTenant) {
      this.getLoggedInUserBannerGql
        .fetch({ variables: {} })
        .subscribe(({ data }) => {
          const bannerInfo = data?.loggedInUser?.bannerInfo;
          const isBannerMessageActive = bannerInfo?.isMessageActive;
          if (isBannerMessageActive) {
            this.bannerMessage.visible = true;
            this.bannerMessage.text = bannerInfo?.message ?? '';
            this.headerMessageStateService.setCustomTenantMessageVisibility(
              isBannerMessageActive
            );
          }
        });
    }
  }

  // Close the banner message and set the visibility to false
  onCloseBannerMessage() {
    this.bannerMessage.visible = false;
    this.bannerMessage.text = '';
    this.headerMessageStateService.setCustomTenantMessageVisibility(false);
  }

  initIdleSetup() {
    this.idle.setIdle(1680); //(28min*60sec) how long can user be inactive before considered idle, in seconds
    this.idle.setTimeout(120); //(2min*60sec) how long can user be idle before considered timed out, in seconds
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES); // provide sources that will "interrupt" aka provide events indicating the user is active
  }

  configureIdleSetup() {
    // set idle parameters
    this.initIdleSetup();

    // show inactivity confirmation dialog when the user becomes idle
    this.idle.onIdleStart.subscribe(() => {
      this.confirmationService.confirm(this.confirmationObj);
    });

    // reset the timeout when some activity happens when the dialog is shown
    this.idle.onIdleEnd.subscribe(() => {
      this.idle.setIdle(1);
      this.currentTimeout--;
      if (this.currentTimeout >= 1) {
        this.idle.setTimeout(this.currentTimeout);
      }
      this.idle.watch();
    });

    // sign out from the inactivity dialog when the user has timed out
    this.idle.onTimeout.subscribe(() => {
      this.signOutFromInactiveConfirmationDialog();
    });

    // update the time in the inactivity dialog when the timeout countdown does its thing
    this.idle.onTimeoutWarning.subscribe((seconds) => {
      this.currentTimeout = seconds;
      const timeout = this.formatInactivityTimeOut(seconds);
      this.confirmationObj.message = `Due to inactivity, you will be automatically signed out in ${timeout}. Click “Continue” to remain signed in.`;
      this.confirmationService.confirm(this.confirmationObj);
      this.cd.detectChanges();
    });
  }

  resetIdleState() {
    // start/reset the idle process and call idle.watch()
    this.initIdleSetup();
    this.idle.watch();
  }

  signOutFromInactiveConfirmationDialog() {
    this.confirmationService.close();
    this.authenticationService.logout();
  }

  formatInactivityTimeOut(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString()}:${seconds.toString().padStart(2, '0')}`;
  }

  stopIdle() {
    this.idle.stop(); // Stop idle monitoring
    this.idle.clearInterrupts(); // Clear any interrupt signals
  }

  onHideInactivityDialog() {
    this.resetIdleState();
  }
}
